import {Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import Point from 'ol/geom/Point';
import Multipoint from 'ol/geom/MultiPoint';
import { PLAYER_RADIUS, BALL_DEFAULT_SIZE, TRAIL_RADIUS, TRAIL_LINE_WIDTH } from '../../../../../config/constants';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import CanvasImmediateRenderer from 'ol/render/canvas/Immediate';
import { Options as FillOptions } from 'ol/style/Fill';
import { Options as StrokeOptions } from 'ol/style/Stroke';
import { Options as TextOptions} from 'ol/style/Text';
import { Coordinate } from 'ol/coordinate';
import { PlayerTrackData } from '../../types';
import Polygon from 'ol/geom/Polygon';

type Context = CanvasRenderingContext2D;
type ToContext = CanvasImmediateRenderer;

const PLAYER_FONT = 'Bold 12px Roboto Condensed';
const SPEED_FONT = 'Normal 11px Roboto Condensed';

const getStroke = (styles: StrokeOptions) => new Stroke(styles);
const getFill = (styles: FillOptions) => new Fill(styles);
const getText = (styles: TextOptions) => new Text(styles);

const playerStroke = getStroke({ color: 'white', width: 2 });
const playerTextFill = getFill({ color: 'white' });
const playerTextStroke = getStroke({ color: 'white', width: 0.2 });

const speedTextFill = getFill({ color: '#343434' });
const speedTextStroke = getStroke({ color: '#343434', width: 0.5 });

export const drawPlayer = (color: string) => {
  const playerFill = getFill({ color });

  return new Style({
    image: new CircleStyle({
      fill: playerFill,
      stroke: playerStroke,
      radius: PLAYER_RADIUS
    }),
  });
};

export const drawPlayerJersey = (playerNumber: string) => {
  const text = getText({ 
    text: playerNumber, 
    font: PLAYER_FONT, 
    fill: playerTextFill, 
    stroke: playerTextStroke, 
    offsetY: 1,
  });

  return new Style({ text });
};

export const drawPlayerSpeedText = (playerSpeed: string) => {
  const playerSpeedText = getText({ 
    text: playerSpeed, 
    font: SPEED_FONT, 
    fill: speedTextFill, 
    stroke: speedTextStroke, 
    offsetY: -17 
  });

  return new Style({
    text: playerSpeedText
  });
};

export const drawPlayerPositionText = (playerPositon: string) => {
  const playerPosText = getText({ 
    text: playerPositon, 
    font: PLAYER_FONT, 
    fill: playerTextFill, 
    stroke: playerTextStroke, 
    offsetY: 19 
  });

  return new Style({
    text: playerPosText
  });
};

export const roundRectangle = (ctx: Context, x: number, y: number, width: number, height: number, radius = 5, fill: boolean) => {
  const radiusDims = { tl: radius, tr: radius, br: radius, bl: radius };
  
  ctx.save();
    ctx.beginPath();
    ctx.fillStyle = "rgba(0,0,0,0.52)";
    ctx.moveTo(x + radiusDims.tl, y);
    ctx.lineTo(x + width - radiusDims.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radiusDims.tr);
    ctx.lineTo(x + width, y + height - radiusDims.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radiusDims.br, y + height);
    ctx.lineTo(x + radiusDims.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radiusDims.bl);
    ctx.lineTo(x, y + radiusDims.tl);
    ctx.quadraticCurveTo(x, y, x + radiusDims.tl, y);
    ctx.closePath();
    if (fill) {
      ctx.fill();
    }
  ctx.restore();
};

export const drawMessageRect = (ctx: Context, x: number, y: number, width: number, height: number) => {
  const arrowDim = width / 3;
  const arrowHeight = height / 4;

  drawMessageOuterShadow(ctx, x, y, width, height);

    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = "rgba(245,245,245,0.9)";
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + height);

    // arrow in the middle
    ctx.lineTo(x + arrowDim, y + height);
    ctx.lineTo(x + arrowDim + (arrowDim / 2), y + height + arrowHeight);
    ctx.lineTo(x + (arrowDim * 2), y + height);

    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x + width, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
};

const drawMessageOuterShadow = (ctx: Context, x: number, y: number, width: number, height: number) => {
  ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(245,245,245,0.9)";

    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = -2;
    ctx.shadowOffsetY = 0;

    ctx.strokeRect(x, y, width, height);
  ctx.restore();
};

export const drawCanvasBall = (ctx: Context, x: number, y: number, canvasPixelRatio: number) => {
  const z = 0.075;
  const updatedSize = BALL_DEFAULT_SIZE * canvasPixelRatio;
  const lineWidth = updatedSize * 0.15;

  ctx.save();
    ctx.beginPath();
    ctx.ellipse(x * canvasPixelRatio, y * canvasPixelRatio, updatedSize / 2, updatedSize, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#795548";
    ctx.strokeStyle = "white";
    ctx.lineWidth = lineWidth;
    ctx.fill();
    ctx.stroke();
    ctx.closePath();

    ctx.beginPath();
    ctx.fillStyle = "white";
    ctx.rect(x * canvasPixelRatio, (y * canvasPixelRatio) - (updatedSize / 2), updatedSize * z, updatedSize);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  ctx.restore();
};

export const drawPlayerSelection = (ctx: Context, x: number, y: number, resolution: number) => {
  ctx.beginPath();
  ctx.arc(x, y, PLAYER_RADIUS * resolution, 0, 2*Math.PI);
  ctx.closePath();
};

export const setPointStyling = (toCtx: ToContext, x: number, y: number, callback: Style) => {
  const drawInCanvas = () => toCtx.drawGeometry(new Point([x, y]));
  toCtx.setStyle(callback);
  drawInCanvas();
};

export const setMultiPointStyling = (toCtx: ToContext, points: Coordinate[], callback: Style) => {
  const drawInCanvas = () => toCtx.drawGeometry(new Multipoint(points));
  toCtx.setStyle(callback);
  drawInCanvas();
};

export const setLineStyling = (toCtx: ToContext, x0: number, y0: number, x1: number, y1: number, callback: Style) => {
  const drawInCanvas = () => toCtx.drawGeometry(new LineString([[x0, y0], [x1, y1]]));
  toCtx.setStyle(callback);
  drawInCanvas();
};

export const setMultiLineStyling = (toCtx: ToContext, lines: Array<Array<Coordinate>>, callback: Style) => {
  const drawInCanvas = () => toCtx.drawGeometry(new MultiLineString(lines));
  toCtx.setStyle(callback);
  drawInCanvas();
};

const getResponsivePlayersSpeed = (x: number, y: number, canvasPixelRatio: number) => {
  return [
    (x - 12) * canvasPixelRatio,
    (y - 24) * canvasPixelRatio, 
    24 * canvasPixelRatio, 
    12 * canvasPixelRatio
  ] as const;
};
export const drawPlayerSpeed = (ctx: Context, toCtx: ToContext, x: number, y: number, overall: string, canvasPixelRatio: number) => {
  const [...args] = getResponsivePlayersSpeed(x, y, canvasPixelRatio);
  drawMessageRect(ctx, ...args);
  setPointStyling(toCtx, x, y, drawPlayerSpeedText('' + overall));
};

const getResponsivePlayersPosition = (x: number, y: number, canvasPixelRatio: number) => {
  return [
    (x - 14) * canvasPixelRatio,
    (y + 10) * canvasPixelRatio,
    28 * canvasPixelRatio, 
    15 * canvasPixelRatio,
    8 * canvasPixelRatio,
    true
  ] as const;
};

export const drawPlayerPosition = (ctx: Context, toCtx: ToContext, x: number, y: number, playerPosition: string, canvasPixelRatio: number) => {
  const [...args] = getResponsivePlayersPosition(x, y, canvasPixelRatio);
  roundRectangle(ctx, ...args);
  setPointStyling(toCtx, x, y, drawPlayerPositionText(playerPosition));
};

export const dotsContainerVariants = {
  hidden: {
    transition: {
      staggerChildren: 0.15
    }
  },
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

export const dotsVariants = {
  hidden: {
    y: '0%'
  },
  visible: {
    y: '100%',
    transition: {
      duration: .5,
      yoyo: Infinity,
      ease: 'easeInOut'
    }
  }
};

export const flexCentered = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 210
};

export const practiceAreaStyles = [
  new Style({
    stroke: new Stroke({
      color: '#009BEC',
      width: 3
    }),
    fill: new Fill({
      color: 'rgba(0, 155, 236, 0.1)'
    })
  }),
  new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({
        color: '#007ABA'
      })
    }),
    geometry: feature => {
      const coordinates = (feature.getGeometry() as Polygon).getCoordinates()[0];
      return new Multipoint(coordinates);
    }
  }),
];

export const selectedAreaStyles = [
  new Style({
    stroke: new Stroke({
      color: '#D0A303',
      width: 3
    }),
    fill: new Fill({
      color: 'rgba(236, 184, 0, 0.1)'
    })
  }),
  new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({
        color: '#C9A72F'
      })
    }),
    geometry: feature => {
      const coordinates = (feature.getGeometry() as Polygon).getCoordinates()[0];
      return new Multipoint(coordinates);
    }
  }),
];

export const drillStyles = (periodTimeline: number, periodInSecs: number, currentSec: number, shouldShowTL: number) => ({
  left: periodTimeline,
  backgroundColor: periodInSecs <= currentSec ? '#2471B7' : '',
  display: periodInSecs - shouldShowTL <= currentSec && currentSec <= periodInSecs + shouldShowTL ? 'none' : ''
});

export const validateSpecialCtxStyling = (ctx: Context, player: PlayerTrackData, currentSec: number, pinnedRows: Array<string>, lastTrackSec: number) => {
  if (currentSec === lastTrackSec) {
    ctx.globalAlpha = 1;
    if (pinnedRows && pinnedRows.length) {
      const isPlayerSelected = pinnedRows.includes(player.playerId);
      if (isPlayerSelected) ctx.globalAlpha = 1;
      else ctx.globalAlpha = 0.55;
    } 
    return player.color;
  } else {
    ctx.globalAlpha = 0.8;
    return '#878787';
  }
};

export const drawTrailPoints = (color: string) => {
  const playerFill = getFill({ color });
  return new Style({
    image: new CircleStyle({
      fill: playerFill,
      radius: TRAIL_RADIUS
    }),
  });
};

export const drawTrailLines = (color: string) => {
  return new Style({
    stroke: new Stroke({
      color,
      width: TRAIL_LINE_WIDTH
    })  
  });
};
